import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { Flex, Text } from '@radix-ui/themes';
import AddAttachment from '../TileForm/components/AddAttachment.jsx';
import fileThumb from '../../assets/missing-file-img-thumb.png';
import styles from './Attachments.module.css';

const getFileExtension = filename => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
};

const Thumbnail = ({ attachments, attachment }) => {
  const extension = attachment.split('.')[attachment.split('.').length - 1];
  switch (extension.toLowerCase()) {
    case 'pdf':
    case 'txt': {
      return (
        <Iframe
          width="84"
          height="84"
          src={attachments[attachment]}
          type="application/pdf"
        />
      );
    }
    case 'mp4':
    case 'mpeg':
    case 'wmv': {
      return <Video src={attachments[attachment]} />;
    }
    case 'bmp':
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'svg': {
      return (
        <Image
          alt={`${attachment.replace(/ /g, '_')}`}
          src={attachments[attachment]}
        />
      );
    }
    case 'zip': {
      return (
        <Image>
          <Icon
            className="fa-regular fa-file-zipper fa-5x"
            aria-hidden="true"
          />
        </Image>
      );
    }
    case 'xls':
    case 'csv':
    case 'xlsx': {
      return (
        <Image>
          <Icon className="fa-regular fa-table fa-5x" aria-hidden="true" />
        </Image>
      );
    }
    case 'doc':
    case 'docx': {
      return (
        <Image>
          <Icon className="fa-regular fa-file-word fa-5x" aria-hidden="true" />
        </Image>
      );
    }
    default: {
      return <Image alt={`${attachment.replace(/ /g, '_')}`} src={fileThumb} />;
    }
  }
};

const Attachments = ({ attachments, removeAttachment, addAttachment }) => {
  const { t } = useTranslation('tileForm');

  return (
    <Wrapper>
      {Object.keys(attachments).map((attachment, key) => {
        const extension = getFileExtension(attachment);
        return (
          <Container
            key={`${attachment.replace(/ /g, '_')}${key}`}
            title={attachment}
          >
            {removeAttachment && (
              <AttachmentDelete
                className="fa-regular fa-trash"
                name={attachment}
                onClick={removeAttachment}
              />
            )}
            <Box
              href={attachments[attachment]}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Thumbnail attachments={attachments} attachment={attachment} />
              <Flex
                direction="column"
                justify="center"
                flexGrow="1"
                minWidth="0"
              >
                <div className={styles.title}>
                  <Text className={styles.attachmentName}>{attachment}</Text>
                </div>
                <Flex align="center" gap="2" mt="1">
                  {extension && (
                    <span className={styles.fileType}>{extension}</span>
                  )}
                  <Text size="2" weight="medium" color="blue">
                    {t('attachment.viewFile')}
                  </Text>
                </Flex>
              </Flex>
              <i
                className={`fa-regular fa-chevron-right ${styles.chevronIcon}`}
                aria-hidden="true"
              />
            </Box>
          </Container>
        );
      })}
      {addAttachment && (
        <AddAttachment
          addAttachmentHandler={addAttachment}
          attachments={attachments}
        />
      )}
    </Wrapper>
  );
};

export default Attachments;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0px;
`;

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Box = styled.a`
  display: flex;
  align-items: center;
  border-radius: 8px;
  border: solid 1px ${themeGet('colors.black.10')};
  margin: 10px 0px;
  cursor: pointer;
  text-decoration: none;
  @media (max-width: 400px) {
    max-width: calc(100vw - 64px);
  }
  &:hover {
    box-shadow: 0px 8px 20px 0px ${themeGet('colors.black.10')};
  }
`;

const Image = styled.div`
  height: 84px;
  min-width: 84px;
  margin: 8px;
  margin-right: 14px;
  border-radius: 8px;
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: center;
`;

const Icon = styled.i`
  color: ${themeGet('colors.primary.100')};
`;

const Iframe = styled.iframe`
  height: 84px;
  min-width: 84px;
  margin: 8px;
  margin-right: 14px;
  border-radius: 8px;
`;

const Video = styled.video`
  height: 84px;
  width: 84px;
  min-width: 84px;
  margin: 8px;
  margin-right: 14px;
  border-radius: 8px;
`;

const AttachmentDelete = styled.i`
  cursor: pointer;
  color: ${themeGet('colors.black.80')};
  position: absolute;
  width: 14px;
  top: 25px;
  right: 8px;
`;
